import { Link, useNavigate } from "react-router-dom";
import {
  SiteIcon,
  UserIcon,
  CloudIcon,
  LogoutIcon,
  BillingIcon,
  SupportIcon,
  FormFieldIcon,
  SettingIcon,
  SingleUserIcon,
  LaunchPadIcon,
  SwitchIcon,
  CheckIcon,
} from "allIcons";
import "./header.css";
import { useEffect, useState } from "react";
import {
  IconContainer,
  ListCard,
  ListIconWrapper,
  Loader,
  LoadingCard,
  PopOver,
  SecondaryHeading,
  UserAvatar,
} from "commonUI";
import { isEqual, usePathname } from "helpers";
import { useUserDetails, useUserLogout, useUserPermissions } from "hooks/user";
import useUserPlan from "hooks/plan";
import { t } from "i18next";
import { useBrandingAndColors } from "hooks/brandingAndColors";
import { useUserAuth } from "context/UserAuth";
import { useWorkSpaces } from "api/workSpaces";
import { queryClient } from "client";
import { clearStore } from "redux/reducers/userAuth";
import { useDispatch } from "react-redux";
import { ModulePrefixes } from "enums";

export default function Header() {
  const [showDrop, setShowDrop] = useState(false);
  const [showChangeWorkspace, setShowChangeWorkspace] = useState(false);
  const [module] = usePathname();
  const { view_templates, view_sites, view_site_template, isLoading: loadingPermissions } = useUserPermissions();
  const { data: wpslPlan = {}, isLoading: loadingPlans } = useUserPlan();
  const isLibraryTemplateAllowed = isEqual(wpslPlan?.temp_access, 1);
  const navigate = useNavigate();
  const { data: brandingData, isLoading: loadingBranding } =
    useBrandingAndColors();
  const { data: workSpaces } = useWorkSpaces();
  const { setUserAuth, userAuth } = useUserAuth();
  const dispatch = useDispatch();

  const { isLoading, data: userDetails } = useUserDetails();

  const mutation = useUserLogout();

  useEffect(() => {
    if (loadingPermissions) return;

    if (!view_sites) {
      // check if path includes sites path "sites" or their is query param web
      if (
        module.includes("sites") ||
        window.location.search.includes("web")
      ) {
        navigate("/dashboard");
      }
    }
    if (!view_site_template) {
      // check if path includes site-settings
      if (
        module.includes(ModulePrefixes.SITE_SETTINGS) && !window.location.search.includes("web")
      ) {
        navigate("/dashboard");
      }
    }
    if (!view_templates) {
      // check if path includes template-library
      if (module.includes(ModulePrefixes.TEMPLATE_LIBRARY)) {
        navigate("/dashboard");
      }
    }
  }, [view_templates, view_sites, view_site_template, loadingPermissions, module]);

  return (
    <>
      <Loader show={mutation.isLoading} />
      <Loader className="bg-white" show={isLoading || loadingBranding} />
      <div className="main-header">
        <Link
          to="/"
          className="flex"
          aria-label={
            brandingData?.brand_colors_settings?.product_name ||
            "WP SiteLauncher"
          }
        >
          <img
            src={
              brandingData?.brand_colors_settings?.product_logo &&
              brandingData?.brand_colors_settings?.product_logo !==
                "https://wpsl-spaces-baucket.ams3.cdn.digitaloceanspaces.com/default-logo.png"
                ? brandingData?.brand_colors_settings?.product_logo
                : require("assets/header-logo.svg").default
            }
            height="35px"
            className="object-contain max-w-[220px]"
            alt={
              brandingData?.brand_colors_settings?.product_name ||
              "WP SiteLauncher"
            }
          />
        </Link>
        <div className="header-left-container">
          {view_sites ? (
            <Link
              to={"sites"}
              title={t("header.menu.sites")}
              className="cursor-pointer"
            >
              <div
                className={`header-icon-container transition-all ${
                  [ModulePrefixes.SITES, ModulePrefixes.SITE_SETTINGS, ModulePrefixes.SITE_TEMPLATE].includes(module)
                    ? "bg-theme-blue"
                    : ""
                }`}
              >
                <SiteIcon />
              </div>
            </Link>
          ) : (
            ""
          )}
          <Link
            to={"users"}
            title={t("header.menu.users")}
            className="cursor-pointer"
          >
            <div
              className={`header-icon-container transition-all ${
                module === "users" ? "bg-theme-blue" : ""
              }`}
            >
              <UserIcon />
            </div>
          </Link>
          {view_templates && isLibraryTemplateAllowed && !loadingPlans ? (
            <Link
              to={`/${ModulePrefixes.TEMPLATE_LIBRARY}`}
              title={t("header.menu.templateLibrary")}
              className="cursor-pointer"
            >
              <div
                className={`header-icon-container transition-all
            ${module === ModulePrefixes.TEMPLATE_LIBRARY ? "bg-theme-blue" : ""}
            `}
              >
                <CloudIcon />
              </div>
            </Link>
          ) : (
            ""
          )}
          <Link
            to={"account-settings"}
            title={t("header.menu.accountSettings")}
            className="cursor-pointer"
          >
            <div
              className={`header-icon-container transition-all ${
                module === "account-settings" ? "bg-theme-blue" : ""
              }`}
            >
              <SettingIcon />
            </div>
          </Link>
          <Link
            to={"dashboard"}
            title={t("dashboard.title")}
            className="cursor-pointer"
          >
            <div
              className={`header-icon-container transition-all ${
                module === "dashboard" || module === "" ? "bg-theme-blue" : ""
              }`}
            >
              <LaunchPadIcon />
            </div>
          </Link>
          <div className="relative">
            <div
              className={`${showDrop ? "dropdown-click-block" : "hidden"}`}
            />
            <div
              className="flex items-center cursor-pointer ml-[11px] relative"
              onClick={() => {
                setShowDrop((pre) => !pre);
              }}
            >
              <div className="header-icon-container">
                <UserAvatar user={userDetails} />
              </div>
              <div className="header-user-container">
                {isLoading ? (
                  <LoadingCard className="h-2 w-14" />
                ) : (
                  <SecondaryHeading className="text-white font-bold ">
                    {userDetails?.user_name ?? userDetails?.name}&nbsp;
                    {userDetails?.last_name}
                  </SecondaryHeading>
                )}
              </div>
              <div className="header-icon-container">
                <FormFieldIcon />
              </div>
              <PopOver
                open={showDrop}
                onClose={() => {
                  setShowDrop(() => false);
                  setShowChangeWorkspace(false);
                }}
                toolTip={{ right: "20px", vertical: "top" }}
                style={{ top: "calc(100% + 7px)", right: "20px" }}
              >
                <div className=" header-user-dropdown">
                  <div
                    className="header-dropdown-content cursor-pointer"
                    onClick={() => {
                      navigate("/users/team-members/" + userDetails.id);
                      setShowDrop(() => false);
                    }}
                  >
                    <IconContainer>
                      <SingleUserIcon />
                    </IconContainer>
                    <span className="body-text m-0">
                      {t("header.menu.editProfile")}
                    </span>
                  </div>
                  <a
                    href="https://wpsitelauncher.com/help"
                    target="_blank"
                    rel="noreferrer"
                    className="header-dropdown-content"
                  >
                    <IconContainer>
                      <SupportIcon />
                    </IconContainer>
                    <span className="body-text m-0">
                      {t("header.menu.helpCenterAndSupport")}
                    </span>
                  </a>
                  <a
                    className="header-dropdown-content"
                    href="https://wpsitelauncher.com/customer-dashboard/?action=index&model=subscription"
                    target="_blank"
                    rel="noreferrer"
                    onMouseEnter={() => setShowChangeWorkspace(false)}
                  >
                    <IconContainer>
                      <BillingIcon />
                    </IconContainer>
                    <span className="body-text m-0">
                      {t("header.menu.managePlanAndBilling")}
                    </span>
                  </a>
                  <div
                    className="header-dropdown-content cursor-pointer relative"
                    onMouseEnter={() => setShowChangeWorkspace(true)}
                  >
                    <IconContainer>
                      <SwitchIcon />
                    </IconContainer>
                    <span className="body-text m-0">
                      {t("header.menu.switchAccounts")}
                    </span>
                    <PopOver
                      open={showChangeWorkspace}
                      onClose={() => {
                        setShowChangeWorkspace(false);
                      }}
                      style={{
                        top: "calc(100% - 30px)",
                        right: "calc(100% + 15px)",
                      }}
                    >
                      <div
                        className="header-workspace-dropdown max-h-64 overflow-y-auto "
                        onMouseLeave={() => setShowChangeWorkspace(false)}
                      >
                        {workSpaces?.map((ele) => {
                          return (
                            <ListCard
                              showTitle
                              data={
                                ele?.featureSetting?.brand_colors_settings
                                  ?.product_name || ele.title
                              }
                              onClick={() => {
                                localStorage.setItem("apikey", ele.key);
                                setUserAuth((pre) => ({
                                  ...pre,
                                  apikey: ele.key,
                                }));
                                queryClient.invalidateQueries();
                                dispatch(clearStore());
                              }}
                              icon={
                                <ListIconWrapper className="border-white text-white bg-theme-card-bg hover:bg-theme-card-bg">
                                  {ele?.featureSetting?.brand_colors_settings
                                    ?.product_logo ? (
                                    <img
                                      src={
                                        ele?.featureSetting
                                          ?.brand_colors_settings?.product_logo
                                      }
                                      alt={
                                        ele?.featureSetting
                                          ?.brand_colors_settings
                                          ?.product_name || ele.title
                                      }
                                      className="w-full h-full object-contain rounded-full"
                                    />
                                  ) : (
                                    ele?.featureSetting?.brand_colors_settings?.product_name[0]?.toUpperCase()
                                  )}
                                </ListIconWrapper>
                              }
                              rightSideContent={
                                userAuth.apikey === ele.key ? (
                                  <ListIconWrapper className="bg-transparent">
                                    <CheckIcon
                                      fill={
                                        ele?.featureSetting
                                          ?.brand_colors_settings
                                          ?.accent_color || "#06f"
                                      }
                                    />
                                  </ListIconWrapper>
                                ) : (
                                  ""
                                )
                              }
                              deleteIcon={true}
                              editIcon={false}
                              secondaryData={`${ele?.owner?.name} ${ele?.owner?.last_name}`}
                            />
                          );
                        })}
                      </div>
                    </PopOver>
                  </div>
                  <div
                    className="header-dropdown-content cursor-pointer"
                    onClick={() => mutation.mutate()}
                    onMouseEnter={() => setShowChangeWorkspace(false)}
                  >
                    <IconContainer>
                      <LogoutIcon />
                    </IconContainer>
                    <span className="body-text m-0">
                      {t("header.menu.logout")}
                    </span>
                  </div>
                </div>
              </PopOver>
            </div>
          </div>
        </div>
      </div>
    </>
  );
}
